import {TFunction} from 'i18next';
import mapValues from 'lodash/mapValues';
import {AnySchema, array, boolean, number, object} from 'yup';
import {ConfiguredField, Field} from '../../../../../shared/model/field.model';
import {LoanConfiguration} from '../../../../../shared/model/loan-configuration.model';
import {
  getConfiguredFieldValidationDefaultSchema,
  getConfiguredFieldValidationSchema
} from '../../../../../utils/step-form-utils/validation-schema/configured-field-validation-schema';
import {
  getOptionalValidationSchema,
  getTermTooBigError,
  getTermTooSmallError,
  getTooBigError,
  getTooSmallError
} from '../../../../../utils/step-form-utils/validation-schema/validation-schema-utils';
import {LoanApplicationDataFormFields, SIMULATION_REQUIRED_INDICATOR_FIELD} from './loan-application-data-form.model';

export const getValidationSchema = (
  formFields: LoanApplicationDataFormFields, loanConfig: LoanConfiguration, t: TFunction
): AnySchema => {

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    validate,
    isPaymentDynamic,
    maxCoMakerNumber,
    minCoMakerNumber,
    maxCoBorrowerNumber,
    minCoBorrowerNumber,
    simulation,
    ...configuredFields
  } = formFields;

  const simulationSchema =
    getOptionalValidationSchema(partialValidation =>
      partialValidation
        ? object()
        : object().when(SIMULATION_REQUIRED_INDICATOR_FIELD, {is: true, then: object().required()}));

  const simulationParamsChangedSchema =
    getOptionalValidationSchema(partialValidation => partialValidation ? boolean() : boolean().isFalse());

  // TODO LOS-595 Common shape of LoanApplicationData and Calculator validation schema
  const getSimulationInputFieldsSchema = (field: ConfiguredField<unknown>): AnySchema => {

    if (field.code === 'TOTAL_AMORTIZATION_NUMBER') {
      const dedicatedSchema = number()
        .nullable()
        .when('$term', ($term, schema) => {
          return schema.test('Check term high range', getTermTooBigError(t, loanConfig.maxTerm, $term), () => {
              return loanConfig.maxTerm >= $term;
            })
            .test('Check term low range', getTermTooSmallError(t, loanConfig.minTerm, $term), () => {
              return loanConfig.minTerm <= $term;
            });
        });

      return getConfiguredFieldValidationSchema(
        false,
        field as Field<string | number>,
        t,
        dedicatedSchema
      );
    }

    if (field.code === 'DIMINISHING_AMORTIZATION_NUMBER') {
      const dedicatedSchema = number()
        .nullable()
        .min(1, getTooSmallError(t, 1))
        .when('$totalAmortizationNumberValue', ($totalAmortizationNumberValue, schema) => {
          return schema.max($totalAmortizationNumberValue, getTooBigError(t, $totalAmortizationNumberValue));
        });

      return getConfiguredFieldValidationSchema(
        false,
        field as Field<string | number>,
        t,
        dedicatedSchema
      );
    }

    return getConfiguredFieldValidationDefaultSchema(false, field as Field<string | number>, t);
  };

  // TODO attach to simulation schema
  const feesArraySchema = array().nullable().of(
    object().nullable().shape({
      fixedAmount: number().min(0).nullable()
    })
  );

  return object({
    isPaymentDynamic: boolean(),
    validate: boolean(),
    term: number().nullable(),
    maxCoMakerNumber: number().nullable(),
    minCoMakerNumber: number().nullable(),
    maxCoBorrowerNumber: number().nullable(),
    minCoBorrowerNumber: number().nullable(),
    simulation: simulationSchema,
    simulationParamsChanged: simulationParamsChangedSchema,
    simulationRequired: boolean(),
    ...mapValues(configuredFields, field => getSimulationInputFieldsSchema(field as Field<string | number>)),
  });
};
